// Registry semantico per i breadcrumb: funzioni pure che classificano le route
// e ricostruiscono la gerarchia canonica (Dashboard > index > show > tab).
// Non importa il router (evita import circolari): l'istanza viene passata come argomento.

const ROOT_PATH = '/dashboard'
const MAX_SEMANTIC_DEPTH = 10

// Override centralizzati per le route fuori standard.
// Chiave = route name. Valori:
//  - type: forza la classificazione ('index' | 'show' | 'tab' | 'edit' | 'create' | 'action')
//  - parent: nome route del padre canonico, oppure funzione (route) => location per i casi parametrici
//  - label: labelKey esplicita (risolta a render time con $trans)
const OVERRIDES = {
	// Viste "sibling di index": si comportano da radice di modulo
	'opportunities-kanban': { type: 'index' },
	'projects-planning': { type: 'index' },
	'whatsapp-chat': { type: 'index' },
	'whatsapp-statistics': { type: 'index' },
	'whatsapp-templates': { type: 'index' },
	'hrme': { type: 'index' },
	'maps': { type: 'index' },
	'notifications': { type: 'index', label: 'Notifiche' },
	'releases-history': { type: 'index', label: 'Storico Rilasci' },
	'edocument-statistics': { type: 'index' },
	'subscriptions-global-statistics': { type: 'index' },
	'leads-duplicates': { type: 'index' },
	'prospects-duplicates': { type: 'index' },
	'callqueues-outcomes': { type: 'index' },
	'hrabsences-inbox': { type: 'index' },
	'hrabsences-calendar': { type: 'index' },
	'hrabsences-balance': { type: 'index' },
	'hrattendance-cartellino': { type: 'index' },
	'hrattendance-timbrature': { type: 'index' },
	'hrattendance-timeline': { type: 'index' },
	'hrcompensation-expenses': { type: 'index' },
	'hrcompensation-payslips': { type: 'index' },
	'hrcompensation-timesheets': { type: 'index' },
	'hrtalent-onboarding': { type: 'index' },
	'hrtalent-reviews': { type: 'index' },
	'hrtalent-trainings': { type: 'index' },
	'hrclocks-widget': { type: 'index' },

	// Documenti: le route generiche usano :doctype, l'index dedicato si risolve dal param
	'documents-show': { parent: route => ({ name: 'documents-' + route.params.doctype + '-index' }) },
	'documents-edit': { parent: route => ({ name: 'documents-' + route.params.doctype + '-index' }) },
	'documents-create': { parent: route => ({ name: 'documents-' + route.params.doctype + '-index' }) },
	'documents-send-mail': { type: 'action', parent: route => ({ name: 'documents-' + route.params.doctype + '-index' }) },
	'edocuments-show': { parent: 'edocument-index' },

	// Statistiche abbonamento: figlia della show
	'subscriptions-statistics': { type: 'tab' },

	// Lotti/matricole: il Lot non ha un campo tra quelli letti da
	// emitBreadcrumbLabel (Show.vue), quindi la show resterebbe sul fallback
	// grezzo derivato dal name ('lots'/'serials'): label esplicite in italiano
	'lots-show': { label: 'Lotto' },
	// niente 'serials-index' (la lista matricole è scoped al prodotto, senza
	// name): il genitore canonico resta la lista lotti globale, come per lots-show
	'serials-show': { label: 'Matricola', parent: 'lots-index' },
	// serials-create: senza override cadrebbe sul fallback generico 'Nuovo' (type
	// 'create', nessuna props.title) e getSemanticParent proverebbe 'serials-index'
	// (inesistente, a differenza di serials-edit che prima tenta 'serials-show'
	// con lo stesso id) — stesso genitore fisso di serials-show.
	'serials-create': { label: 'Nuova Matricola', parent: 'lots-index' },

	// Azioni e pagine senza meta.title né props.title: label esplicite
	'saleorders-process': { label: 'Evasione Ordine' },
	'purchaseorders-process': { label: 'Evasione Ordine' },
	'saleorders-generate-purchaseorders': { label: 'Genera Ordini di Acquisto' },
	'ddts-restore': { label: 'Ripristino DDT' },
	'ddts-customer-return': { label: 'Reso da cliente' },
	'purchaseddts-supplier-return': { label: 'Reso a fornitore' },
	'purchaseddts-unload': { label: 'Scarico DDT' },
	'companiesmailsschedules-show': { label: 'Regola Invio Mail' },
	'companiesmailsschedules-simulate': { label: 'Simulazione' },
	'quotationsmailsschedules': { label: 'Regola Invio Mail' },
	'templates-mailmarketing-builder': { label: 'Builder Template' },
	'templates-mailmarketing-ai-builder': { label: 'Builder Template' },
	'templates-signature-show': { label: 'Firma Mail' },
	'hrclock-deeplink': { label: 'Timbratura' },

	// Sezioni settings: le foglie sono spesso senza name e senza meta.title,
	// il name sta sul wrapper — label esplicite coerenti con il menu Impostazioni
	'settingsagents': { label: 'Impostazioni Agenti' },
	'settingsanalytics': { label: 'Impostazioni Statistiche' },
	'settingsassets': { label: 'Impostazioni Asset' },
	'settingscalendar': { label: 'Impostazioni Calendario' },
	'settingscallcenter': { label: 'Impostazioni Call Center' },
	'settingscommercial': { label: 'Impostazioni Commerciale' },
	'settingscompanies': { label: 'Impostazioni Aziende' },
	'settingscontainers': { label: 'Impostazioni Cassoni' },
	'settingsdashboard': { label: 'Impostazioni Dashboard' },
	'settingsddts': { label: 'Impostazioni DDT' },
	'settingsdocuments': { label: 'Impostazioni Fatture' },
	'settingsgeneral': { label: 'Impostazioni Generali' },
	'settingshelpdesk': { label: 'Impostazioni Helpdesk' },
	'settingsleads': { label: 'Impostazioni Lead' },
	'settingslooms': { label: 'Impostazioni Telai' },
	'settingsmailmarketing': { label: 'Impostazioni Mail Marketing' },
	'settingsopportunities': { label: 'Impostazioni Opportunità' },
	'settingsorders': { label: 'Impostazioni Ordini' },
	'settingsproduction': { label: 'Impostazioni Produzione' },
	'settingsproducts': { label: 'Impostazioni Prodotti' },
	'settingsprojects': { label: 'Impostazioni Progetti' },
	'settingsprospects': { label: 'Impostazioni Prospect' },
	'settingsmobile': { label: 'Impostazioni Mobile' },
	'settingsquotations': { label: 'Impostazioni Preventivi' },
	'settingsquotationsscheduling': { label: 'Impostazioni Regole Invio Preventivi' },
	'settingsroles': { label: 'Impostazioni Ruoli' },
	'settingssubscriptions': { label: 'Impostazioni Abbonamenti' },
	'settingssuppliers': { label: 'Impostazioni Fornitori' },
	'settingstasks': { label: 'Impostazioni Task' },
	'settingstickets': { label: 'Impostazioni Ticket' },
	'settingstimecards': { label: 'Impostazioni Timecard' },
	'settingsusers': { label: 'Impostazioni Utenti' },
	'settingsvehicles': { label: 'Impostazioni Veicoli' },
	'settingswhatsapp': { label: 'Impostazioni WhatsApp' },
}

// Correzioni per label senza traduzione (né locale né backend): si applicano a
// meta.title, props.title e ai fallback su name/segmento di path
const LABEL_FIXES = {
	supplymanagement: 'Approvvigionamenti',
	variants: 'Varianti',
	components: 'Componenti',
	woocommerce: 'WooCommerce',
	mailup: 'MailUp',
	prestashop: 'PrestaShop',
	shopify: 'Shopify',
	'send-multiple-mails': 'Invio Email Multiplo',
	generaltypes: 'Tipologie',
	'templates-mailmarketing': 'Template Mail Marketing',
	lists: 'Lista WhatsApp',
}

function applyLabelFix(label) {
	const fixed = LABEL_FIXES[String(label).toLowerCase()]
	return fixed || String(label)
}

// Label delle sezioni di menu (crumb radice) quando il segmento di path
// non ha una chiave di traduzione. La voce di menu reale (getPermissions) viene
// risolta a render time in Breadcrumb.vue quando labelKey coincide con lo slug.
const SECTION_LABELS = {}

function normalizePath(path) {
	if (!path) return path
	let normalized = path.split('?')[0].split('#')[0]
	if (normalized.length > 1 && normalized.endsWith('/')) normalized = normalized.slice(0, -1)
	return normalized
}

function isCatchAll(resolvedRoute) {
	return !resolvedRoute
		|| resolvedRoute.matched.length === 0
		|| resolvedRoute.matched.some(record => record.path === '*' || typeof record.redirect !== 'undefined')
}

// Cache dei route name esistenti, per evitare i warning di router.resolve su name inesistenti
const namedRoutesCache = new WeakMap()
function collectNames(routes, names) {
	routes.forEach(record => {
		if (record.name) names.add(record.name)
		if (record.children) collectNames(record.children, names)
	})
}
function routeNameExists(router, name) {
	if (!namedRoutesCache.has(router)) {
		const names = new Set()
		collectNames(router.options.routes || [], names)
		namedRoutesCache.set(router, names)
	}
	return namedRoutesCache.get(router).has(name)
}

function safeResolve(router, location) {
	try {
		if (location && location.name && !routeNameExists(router, location.name)) return null
		const resolved = router.resolve(location)
		return isCatchAll(resolved.route) ? null : resolved.route
	} catch (e) {
		return null
	}
}

// Path del primo record matched coi params interpolati (es. '/companies/:id' -> '/companies/42').
// Identifica il "gruppo" (wrapper di tab) a cui appartiene la route.
export function getGroupKey(route) {
	if (!route.matched || route.matched.length === 0) return normalizePath(route.path)
	const wrapperPath = route.matched[0].path
	const interpolated = wrapperPath
		.split('/')
		.map(segment => {
			if (!segment.startsWith(':')) return segment
			const paramName = segment.replace(/^:([^(?*+]+).*$/, '$1')
			const value = route.params[paramName]
			return typeof value === 'undefined' ? segment : value
		})
		.join('/')
	return normalizePath(interpolated)
}

function endsWithNumericSegment(path) {
	const segments = normalizePath(path).split('/')
	return /^\d+$/.test(segments[segments.length - 1])
}

export function classifyRoute(route) {
	const override = route.name && OVERRIDES[route.name]
	if (override && override.type) return override.type

	if (route.name) {
		if (route.name.endsWith('-index')) return 'index'
		if (route.name.endsWith('-show')) return 'show'
		if (route.name.endsWith('-edit')) return 'edit'
		if (route.name.endsWith('-create')) return 'create'
		if (route.name.startsWith('settings')) return 'index'
	}

	const groupKey = getGroupKey(route)
	if (endsWithNumericSegment(groupKey)) {
		// Route dentro un wrapper montato su :id (show o tab figlio, spesso senza name)
		return normalizePath(route.path) === groupKey ? 'show' : 'tab'
	}

	if (typeof route.params.id !== 'undefined') return 'action'

	return 'index'
}

// Testo del tab corrispondente alla route corrente, cercato nei props.tabs
// dei wrapper matched (copre i tab senza name e senza meta.title, es. settings)
function getTabText(route) {
	const routePath = normalizePath(route.path)
	for (let i = route.matched.length - 1; i >= 0; i--) {
		const record = route.matched[i]
		let props = record.props && (typeof record.props.default !== 'undefined' ? record.props.default : record.props)
		try {
			if (typeof props === 'function') props = props(route)
		} catch (e) {
			props = null
		}
		const tabs = props && props.tabs
		if (Array.isArray(tabs)) {
			const tab = tabs.find(t => {
				const to = t && (typeof t.to === 'string' ? t.to : t.to && t.to.path)
				return to && normalizePath(to) === routePath
			})
			if (tab && tab.text) return String(tab.text)
		}
	}
	return null
}

// props.title del record più profondo che ce l'ha: molte route senza meta.title
// passano comunque un titolo al componente (Crud, SendMail, azioni...)
function getPropsTitle(route) {
	for (let i = route.matched.length - 1; i >= 0; i--) {
		const record = route.matched[i]
		let props = record.props && (typeof record.props.default !== 'undefined' ? record.props.default : record.props)
		try {
			if (typeof props === 'function') props = props(route)
		} catch (e) {
			props = null
		}
		if (props && typeof props.title === 'string' && props.title) return props.title
	}
	return null
}

function getLabelKey(route) {
	// override esatto sul name della route
	const exactOverride = route.name && OVERRIDES[route.name]
	if (exactOverride && exactOverride.label) return exactOverride.label

	// meta.title del record più profondo che ce l'ha
	for (let i = route.matched.length - 1; i >= 0; i--) {
		const meta = route.matched[i].meta
		if (meta && meta.title) return applyLabelFix(meta.title)
	}

	// tab secondario di una sezione: il testo del tab nei props del wrapper
	// (es. "Listini Anagrafici" in settingsproducts)
	const isSectionRoot = getGroupKey(route) === normalizePath(route.path)
	const tabText = getTabText(route)
	if (!isSectionRoot && tabText) return tabText

	// override del record con name più vicino alla foglia (le foglie dei settings
	// sono senza name: il name sta sul wrapper, es. "Impostazioni Prodotti")
	for (let i = route.matched.length - 1; i >= 0; i--) {
		const override = route.matched[i].name && OVERRIDES[route.matched[i].name]
		if (override && override.label) return override.label
	}

	const propsTitle = getPropsTitle(route)
	if (propsTitle) return applyLabelFix(propsTitle)

	if (tabText) return tabText

	// fallback generici per tipo: il contesto lo dà il crumb precedente
	const type = classifyRoute(route)
	if (type === 'edit') return 'Modifica'
	if (type === 'create') return 'Nuovo'
	if (type === 'show' && route.name) {
		const match = route.name.match(/^(.+)-show$/)
		if (match) return match[1]
	}

	if (route.name) return applyLabelFix(route.name)

	const segments = normalizePath(route.path).split('/').filter(s => s && !/^\d+$/.test(s))
	return segments.length ? applyLabelFix(segments[segments.length - 1]) : route.path
}

export function makeCrumb(route) {
	return {
		path: normalizePath(route.path),
		fullPath: route.fullPath,
		name: route.name || null,
		type: classifyRoute(route),
		groupKey: getGroupKey(route),
		labelKey: getLabelKey(route),
		entityLabel: null,
	}
}

// Crumb sintetico per la sezione di menu (wrapper di primo livello): radice fissa
// sopra ogni index, anche sul tab principale (es. "DDT > DDT di Vendita"), così i
// tab non cambiano posizione tra un tab e l'altro. Può avere lo stesso path
// dell'index (il dedup del trail cerca dall'ultimo verso il primo).
function makeSectionCrumb(route) {
	const record = route.matched && route.matched[0]
	if (!record) return null
	const groupKey = getGroupKey(route)
	if (groupKey === ROOT_PATH) return null
	const segments = groupKey.split('/').filter(Boolean)
	if (segments.length !== 1) return null
	const override = record.name && OVERRIDES[record.name]
	const labelKey = (override && override.label)
		|| SECTION_LABELS[segments[0]]
		|| segments[0]
	return {
		path: groupKey,
		fullPath: groupKey + '/',
		name: record.name || null,
		type: 'section',
		groupKey: groupKey,
		labelKey: labelKey,
		entityLabel: null,
	}
}

export function isExcluded(route) {
	if (['calendar', 'login', 'impersonate', 'notfound'].includes(route.name)) return true
	if (route.matched && route.matched.some(record => record.meta && record.meta.portal)) return true
	return normalizePath(route.path).startsWith('/portal')
}

// Padre canonico di una route, come route risolta (o null se il padre è la Dashboard).
export function getSemanticParent(route, router) {
	const override = route.name && OVERRIDES[route.name]
	if (override && typeof override.parent !== 'undefined') {
		const location = typeof override.parent === 'function'
			? override.parent(route)
			: { name: override.parent }
		const resolved = safeResolve(router, location)
		if (resolved) return resolved
	}

	const type = classifyRoute(route)

	// Convenzione sui name: X-show/X-edit/X-create -> X-index (per le edit prima si tenta X-show con lo stesso id)
	if (route.name) {
		const match = route.name.match(/^(.+)-(show|edit|create)$/)
		if (match) {
			const slug = match[1]
			if (match[2] === 'edit' && typeof route.params.id !== 'undefined') {
				const show = safeResolve(router, { name: slug + '-show', params: { ...route.params } })
				if (show) return show
			}
			const index = safeResolve(router, { name: slug + '-index' })
			if (index) return index
		}
	}

	// Tab figlio di un wrapper su :id -> la show del wrapper
	if (type === 'tab') {
		const show = safeResolve(router, getGroupKey(route))
		if (show && normalizePath(show.path) !== normalizePath(route.path)) return show
	}

	// Troncamento del path: risali i segmenti finché non si risolve una route reale
	let currentPath = normalizePath(route.path)
	let guard = 0
	while (guard++ < MAX_SEMANTIC_DEPTH) {
		const segments = currentPath.split('/').filter(Boolean)
		if (segments.length <= 1) break
		segments.pop()
		currentPath = '/' + segments.join('/')
		const resolved = safeResolve(router, currentPath)
		if (resolved && !isExcluded(resolved) && normalizePath(resolved.path) !== normalizePath(route.path)) {
			return resolved
		}
	}

	return null
}

// Trail canonico [index, ..., route] per deep-link, refresh, nuova scheda.
// La Dashboard non fa parte del trail: la radice è l'index del modulo.
export function buildSemanticTrail(route, router) {
	const trail = []
	let current = route
	let guard = 0
	while (current && guard++ < MAX_SEMANTIC_DEPTH) {
		const crumb = makeCrumb(current)
		if (crumb.path === ROOT_PATH) break
		trail.unshift(crumb)
		// gli index sono radici di modulo: sopra di loro c'è solo la sezione di menu,
		// mai un altro index fratello (es. niente "Ordini di Vendita" sopra "Ordini di Acquisto")
		if (crumb.type === 'index') {
			const section = makeSectionCrumb(current)
			// le label identiche adiacenti (es. "Aziende > Aziende") vengono collassate
			// a render time in Breadcrumb.vue
			if (section) trail.unshift(section)
			break
		}
		const parent = getSemanticParent(current, router)
		if (!parent || normalizePath(parent.path) === normalizePath(current.path)) break
		current = parent
	}
	return trail
}
